import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Grid, TextField, Typography, Alert, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { authService, stripeService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const onlyDigits = (v) => (v || '').replace(/\D/g, '');

const formatCPF = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return d.replace(/(\d{3})(\d{0,3})/, '$1.$2');
  if (d.length <= 9) return d.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3');
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
};

const formatCelular = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return d.replace(/(\d{2})(\d{0,5})/, '($1) $2');
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return d.replace(/(\d{2})(\d{1})(\d{4})(\d{0,4})/, '($1) $2 $3-$4');
};

const formatCEP = (v) => {
  const d = onlyDigits(v).slice(0, 8);
  if (d.length <= 5) return d;
  return d.replace(/(\d{5})(\d{0,3})/, '$1-$2');
};

const CadastroAssinaturaStripe = () => {
  const navigate = useNavigate();
  const { login: loginContext } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showFullForm, setShowFullForm] = useState(false);
  const [showSenha, setShowSenha] = useState(false);
  const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);

  const [form, setForm] = useState({
    email: '',
    senha: '',
    confirmarSenha: '',
    nome: '',
    cpf: '',
    celular: '',
    cep: '',
    endereco: '',
    bairro: '',
    cidade: '',
    uf: '',
    numero: '',
    complemento: ''
  });

  const cepDigits = useMemo(() => onlyDigits(form.cep), [form.cep]);

  useEffect(() => {
    const run = async () => {
      if (cepDigits.length !== 8) return;
      setError('');
      try {
        const resp = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`);
        const data = await resp.json();
        if (data?.erro) return;
        setForm((prev) => ({
          ...prev,
          endereco: data?.logradouro || prev.endereco,
          bairro: data?.bairro || prev.bairro,
          cidade: data?.localidade || prev.cidade,
          uf: data?.uf || prev.uf
        }));
      } catch (_) {
      }
    };

    run();
  }, [cepDigits]);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const iniciarCheckout = async () => {
    const resp = await stripeService.createCheckoutSession();
    const url = resp?.data?.url || resp?.data?.data?.url;
    if (!url) throw new Error('Não foi possível iniciar o checkout');
    window.location.href = url;
  };

  const handleContinuar = async () => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      const resp = await loginContext(form.email, form.senha);
      if (!resp?.success) {
        setShowFullForm(true);
        throw new Error(resp?.message || 'Não foi possível entrar');
      }
      await iniciarCheckout();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Não foi possível continuar';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCadastrarEPagar = async () => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      if (!form.email?.trim() || !form.senha?.trim() || !form.nome?.trim()) {
        throw new Error('Preencha e-mail, senha e nome');
      }
      if (form.senha !== form.confirmarSenha) {
        throw new Error('As senhas não conferem');
      }
      if (onlyDigits(form.cpf).length !== 11) {
        throw new Error('CPF inválido');
      }
      if (onlyDigits(form.celular).length < 10) {
        throw new Error('Celular inválido');
      }
      if (onlyDigits(form.cep).length !== 8) {
        throw new Error('CEP inválido');
      }
      if (!form.numero?.trim()) {
        throw new Error('Informe o número');
      }

      const payload = {
        email: form.email.trim(),
        senha: form.senha,
        userName: form.nome.trim(),
        cpf: onlyDigits(form.cpf),
        celular: onlyDigits(form.celular),
        cep: onlyDigits(form.cep),
        endereco: form.endereco?.trim() || '',
        bairro: form.bairro?.trim() || '',
        cidade: form.cidade?.trim() || '',
        uf: form.uf?.trim() || '',
        numero: form.numero?.trim() || '',
        complemento: form.complemento?.trim() || ''
      };

      const regResp = await fetch('https://trajetus.net/api/auth/registrar-assinatura', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const regJson = await regResp.json().catch(() => ({}));
      if (!regResp.ok || !regJson?.success) {
        const message = regJson?.message || 'Não foi possível concluir o cadastro';
        if (regResp.status === 409 && String(message).toLowerCase().includes('cpf')) {
          setInfo('Este CPF já está cadastrado. Para sua segurança, redefina a senha por e-mail e tente novamente.');
        }
        throw new Error(message);
      }

      const loginResp = await loginContext(form.email, form.senha);
      if (!loginResp?.success) {
        throw new Error(loginResp?.message || 'Cadastro concluído, mas não foi possível entrar');
      }

      await iniciarCheckout();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Erro ao cadastrar';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSenha = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await authService.forgotPassword(form.email);
      if (resp?.success) {
        setInfo(resp?.message || 'Se o e-mail existir, enviaremos um link de redefinição.');
      } else {
        setError(resp?.message || 'Não foi possível enviar o link agora');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Não foi possível enviar o link agora');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', mt: 4, px: 2 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              component="img"
              src="/logo_header.png"
              alt="Trajetus"
              onClick={() => navigate('/')}
              sx={{ height: 44, width: 'auto', cursor: 'pointer' }}
            />
          </Box>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Assinar plano PRO
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Plano mensal: R$ 59,99
          </Typography>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {info ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {info}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="E-mail" value={form.email} onChange={(e) => update('email', e.target.value)} disabled={loading} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                label="Senha"
                type={showSenha ? 'text' : 'password'}
                value={form.senha}
                onChange={(e) => update('senha', e.target.value)}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSenha((v) => !v)}
                        edge="end"
                        aria-label={showSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        {showSenha ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {showFullForm ? (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    label="Repetir senha"
                    type={showConfirmarSenha ? 'text' : 'password'}
                    value={form.confirmarSenha}
                    onChange={(e) => update('confirmarSenha', e.target.value)}
                    disabled={loading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmarSenha((v) => !v)}
                            edge="end"
                            aria-label={showConfirmarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                          >
                            {showConfirmarSenha ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="Nome" value={form.nome} onChange={(e) => update('nome', e.target.value)} disabled={loading} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="CPF" value={form.cpf} onChange={(e) => update('cpf', formatCPF(e.target.value))} disabled={loading} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="Celular" value={form.celular} onChange={(e) => update('celular', formatCelular(e.target.value))} disabled={loading} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="CEP" value={form.cep} onChange={(e) => update('cep', formatCEP(e.target.value))} disabled={loading} />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="Endereço" value={form.endereco} onChange={(e) => update('endereco', e.target.value)} disabled={loading} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="Bairro" value={form.bairro} onChange={(e) => update('bairro', e.target.value)} disabled={loading} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="Cidade" value={form.cidade} onChange={(e) => update('cidade', e.target.value)} disabled={loading} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="UF" value={form.uf} onChange={(e) => update('uf', e.target.value.toUpperCase().slice(0, 2))} disabled={loading} />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="Número" value={form.numero} onChange={(e) => update('numero', e.target.value)} disabled={loading} />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField fullWidth variant="outlined" InputLabelProps={{ shrink: true }} label="Complemento" value={form.complemento} onChange={(e) => update('complemento', e.target.value)} disabled={loading} />
                </Grid>
              </>
            ) : null}

            <Grid item xs={12}>
              {!showFullForm ? (
                <Button variant="contained" fullWidth onClick={handleContinuar} disabled={loading}>
                  {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                  Continuar
                </Button>
              ) : (
                <Button variant="contained" fullWidth onClick={handleCadastrarEPagar} disabled={loading}>
                  {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                  Cadastrar e pagar
                </Button>
              )}
            </Grid>

            {showFullForm ? (
              <Grid item xs={12}>
                <Button variant="text" fullWidth onClick={handleResetSenha} disabled={loading}>
                  Esqueci minha senha
                </Button>
              </Grid>
            ) : null}

            {!showFullForm ? (
              <Grid item xs={12}>
                <Button variant="text" fullWidth onClick={() => setShowFullForm(true)} disabled={loading}>
                  Não tenho cadastro
                </Button>
              </Grid>
            ) : null}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CadastroAssinaturaStripe;
